import { create } from 'zustand';
import { getActiveTrellusClient } from './host-runtime';
import { TrellusSidebarPayloadSchema, type TrellusSidebarPayload } from './host-schemas';
import type { TrellusFailureReason } from './host-client';
import { hasTrellusCommand, useTrellusHostStore } from './trellus-host-store';

/**
 * Open/closed state of the Trellus dialer sidebar, as last REPORTED by
 * Trellus (pushed `SIDEBAR_STATE` or a `sidebar.*` result). Never optimistic:
 * `open` only changes when Trellus says so.
 *
 * The three actions are capability-gated: with no `sidebar.*` advertised
 * (extension absent, or a pre-v15 build) they are no-ops that return
 * `{ status: 'unsupported' }` — the header icon stays hidden and Trellus's own
 * pill remains the fallback. They control panel visibility only; they never
 * place a call, pause a queue or complete a task.
 */

export type TrellusSidebarGongState = TrellusSidebarPayload['gong_state'];

export interface TrellusSidebarRejection {
  reason_code: string;
  /** e.g. `sidebar_busy` (a call-affecting action is pending) is retryable. */
  retryable: boolean;
}

export interface TrellusSidebarState {
  /** null until Trellus has reported. */
  open: boolean | null;
  /** Highest `state_sequence` seen this document; -1 = none. Older reports are ignored. */
  state_sequence: number;
  gong_state: TrellusSidebarGongState | null;
  /** A sidebar command is in flight. */
  busy: boolean;
  last_rejection: TrellusSidebarRejection | null;
}

export const initialState: TrellusSidebarState = {
  open: null,
  state_sequence: -1,
  gong_state: null,
  busy: false,
  last_rejection: null,
};

export const useTrellusSidebarStore = create<TrellusSidebarState>(() => ({ ...initialState }));

const set = useTrellusSidebarStore.setState;

/** Apply a report from Trellus. Reports older than what we hold are ignored. Returns whether state changed. */
export function applyTrellusSidebarState(report: TrellusSidebarPayload): boolean {
  if (report.state_sequence < useTrellusSidebarStore.getState().state_sequence) return false;
  set({ open: report.open, state_sequence: report.state_sequence, gong_state: report.gong_state, last_rejection: null });
  return true;
}

/** New document identity (extension reloaded): its sequence restarts, so ours must too. */
export function resetTrellusSidebar(): void {
  set(initialState, true);
}

export type TrellusSidebarActionResult =
  | { status: 'applied'; open: boolean }
  | { status: 'unsupported' }
  /** Another sidebar command is still in flight; nothing was sent. */
  | { status: 'busy' }
  | { status: 'rejected'; reason_code: string; retryable: boolean }
  | { status: 'failed'; reason: TrellusFailureReason | 'invalid_response' };

type SidebarCommand = 'sidebar.open' | 'sidebar.close' | 'sidebar.toggle';

async function runSidebarCommand(command: SidebarCommand): Promise<TrellusSidebarActionResult> {
  const client = getActiveTrellusClient();
  if (!client || !hasTrellusCommand(useTrellusHostStore.getState(), command)) return { status: 'unsupported' };
  if (useTrellusSidebarStore.getState().busy) return { status: 'busy' };

  set({ busy: true, last_rejection: null });
  const result = await client.sendCommand(command, {});
  set({ busy: false });

  if (!result.ok) {
    if (result.reason === 'unsupported') return { status: 'unsupported' };
    if (result.reason === 'rejected') {
      const rejection = { reason_code: result.reason_code ?? 'command_rejected', retryable: result.retryable === true };
      set({ last_rejection: rejection });
      return { status: 'rejected', ...rejection };
    }
    return { status: 'failed', reason: result.reason };
  }
  const payload = TrellusSidebarPayloadSchema.safeParse(result.value);
  if (!payload.success) return { status: 'failed', reason: 'invalid_response' };
  applyTrellusSidebarState(payload.data);
  return { status: 'applied', open: payload.data.open };
}

export const openTrellusSidebar = (): Promise<TrellusSidebarActionResult> => runSidebarCommand('sidebar.open');
export const closeTrellusSidebar = (): Promise<TrellusSidebarActionResult> => runSidebarCommand('sidebar.close');
export const toggleTrellusSidebar = (): Promise<TrellusSidebarActionResult> => runSidebarCommand('sidebar.toggle');

/** Prime state after the handshake (`sidebar.get_state`). Silent no-op when unsupported. */
export async function refreshTrellusSidebarState(): Promise<void> {
  const client = getActiveTrellusClient();
  if (!client || !hasTrellusCommand(useTrellusHostStore.getState(), 'sidebar.get_state')) return;
  const result = await client.sendCommand('sidebar.get_state', {});
  if (!result.ok) return;
  const payload = TrellusSidebarPayloadSchema.safeParse(result.value);
  if (payload.success) applyTrellusSidebarState(payload.data);
}
